// ONE WALLET AND ITS MOVES (the wallet's P-073, dc62595d, 3 Nov 2026): custody.walletMoves — the custody moves from or to one desk
// wallet and the deposits that landed on it, newest first. Opened from custody's Wallets (a wallet's name) and from the money
// screen (a role's wallet). In and out by the other end's register name, else its address; the figure signed; a state only when
// the move is not done. A 404 — not a desk wallet, or a server before P-073 — is "not available yet", never a failure.
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { custody, type WalletBalance, type WalletMove } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { readState } from '../../../lib/read-state';
import { fmtStamp } from '../../../lib/format';
import { Figure } from './parts';
import { shortAddress, walletName } from '../../../lib/custody-format';
import { DetailSheet, EmptyState, ReadFailed, SkeletonRows, StateLine, TAP } from '../kit';
import { Pill } from '../../Pill';

/** A move's state when it is not done: on its way, or failed. Done says nothing. */
function MoveState({ m }: { m: WalletMove }) {
  if (m.status === 'swept') return null;
  if (['failed', 'expired', 'rejected'].includes(m.status)) return <Pill tone="bad">{m.status === 'rejected' ? 'Rejected' : 'Failed'}</Pill>;
  return <Pill tone="neutral">On its way</Pill>;
}

/** ONE WALLET AND ITS MOVES (P-073): the wallet as last read, then its custody moves and the deposits that landed on it, newest first. */
export function WalletMovesSheet({ w, head, onBack, onClose }: { w: WalletBalance; head: ReactNode; onBack: (() => void) | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ['custody', 'wallet-moves', w.id], queryFn: () => custody.walletMoves(w.id, 50), retry: false });
  // 404: not a desk wallet — and every id before the Books server that carries P-073.
  const notYet = q.error instanceof ApiError && q.error.status === 404;
  const state = readState(q);
  return (
    <DetailSheet title={walletName(w)} onClose={onClose}>
      {onBack && <button type="button" className={`${TAP} mb-2 text-meta`} onClick={onBack}>← All {walletName(w).split(' · ')[0]}</button>}
      {head}
      <h3 className="mb-1 mt-5 text-label font-medium uppercase tracking-wider text-tg-hint">Moves</h3>
      {notYet ? <StateLine>Moves not available yet.</StateLine>
        : state === 'failed' ? <ReadFailed what="the moves" error={q.error} onRetry={() => void q.refetch()} />
        : !q.data ? <SkeletonRows rows={3} what="the moves" />
        : q.data.moves.length === 0 ? <EmptyState>No moves yet.</EmptyState>
        : (
          <ul>
            {q.data.moves.map((m) => (
              <li key={`${m.kind}-${m.id}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 border-b border-stroke-soft py-3 last:border-b-0">
                <span className="min-w-0">
                  <span className="block font-mono tabular text-meta text-tg-hint">{fmtStamp(m.at)}</span>
                  <span className="block break-words text-body">
                    {m.direction === 'in' ? (m.kind === 'deposit' ? 'Deposit from ' : 'In from ') : 'Out to '}
                    {m.other.name ?? <span className="font-mono">{shortAddress(m.other.address, 6, 4)}</span>}
                  </span>
                </span>
                <span className="text-right">
                  <span className={`block whitespace-nowrap font-mono tabular text-body ${m.direction === 'in' ? 'text-pos' : ''}`}>{m.direction === 'in' ? '+' : '−'}<Figure value={m.amount} asset={m.asset} /></span>
                  <span className="mt-1 block"><MoveState m={m} /></span>
                </span>
              </li>
            ))}
          </ul>
        )}
      {q.data && q.data.moves.length > 0 && <p className="mt-3 text-meta text-tg-hint">Newest first · Dubai time</p>}
    </DetailSheet>
  );
}

